import React, { useEffect, useRef, useState } from "react";
import {
  validatorListIcons,
} from "../../utils/constants";
import { Button } from "../../components";
import { formatNumber, getValidatorImg } from "../../utils/helper";
import { useNavigate } from "react-router-dom";
import ClipLoaderComponent from "../loader/ClipLoaderComponent";

export default function ValidatorDataTable({
  filteredAndSortedValidators = [],
  setSelectedValidator,
  isUndefined,
  setHasMore,
  hasMore,
  setIsLoadingMore,
  view,
  isLoadingMore,
}) {
  const navigate = useNavigate();
  const tableDivRef = useRef(null); // Create a ref for the main table div

  const handleDelegate = (
    title,
    performance,
    commissionValue,
    contractAddress
  ) => {

    const obj = {
      name: title,
      performance: (performance ? performance * 100 : 0) + "%",
      commission: (commissionValue ? commissionValue * 100 : 0) + "%",
      contract_address: contractAddress,
    };
    setSelectedValidator(obj);
    localStorage?.setItem("selectedValidator", JSON.stringify(obj));
    navigate("/delegate");
  };

  const handleValidator = (validator) => {
    if (validator?.commission === 0 ||  validator?.status === "inactive") {
      return
    } else {
      const obj = {
        name: validator?.name,
        performance: validator?.performance,
        commission: `${validator?.commission * 100}%`,
        contract_address: validator?.contract_address,
        checkpoint_signed: validator?.checkpoint_signed,
        total_staked: validator?.total_staked,
        total_rewards_distributed: validator?.total_rewards_generated,
        health_status: "Healthy",
        validator_stake: validator?.validator_stake,
        delegators: validator?.delegators,
        status: validator?.status,
        ownerWallet: validator?.owner_wallet_address
      };
      setSelectedValidator(obj);
      localStorage?.setItem("selectedValidator", JSON.stringify(obj));
      navigate(`/validator/${validator?.contract_address}`);
    }
  };

  const [sortConfig, setSortConfig] = useState({
    key: "",
    direction: "ascending",
  }); // Sorting state

  const handleSort = (key) => {
    if (sortConfig.key === key) {
      // Toggle between ascending, descending, and reset (default state)
      if (sortConfig.direction === "ascending") {
        setSortConfig({ key, direction: "descending" });
      } else if (sortConfig.direction === "descending") {
        setSortConfig({ key: "", direction: "" }); // Reset to default state
      }
    } else {
      setSortConfig({ key, direction: "ascending" });
    }
  };
  const sortedValidators = React.useMemo(() => {
    const validData = Array.isArray(filteredAndSortedValidators)
      ? filteredAndSortedValidators
      : [];
    let sortableData = [...validData];

    if (sortConfig.key) {
      sortableData.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        // Check if the values being compared are strings
        if (typeof aValue === "string" && typeof bValue === "string") {
          // Normalize case for comparison but keep the original case for display
          const normalizedA = aValue.toUpperCase();
          const normalizedB = bValue.toUpperCase();

          if (normalizedA < normalizedB) {
            return sortConfig.direction === "ascending" ? -1 : 1;
          }
          if (normalizedA > normalizedB) {
            return sortConfig.direction === "ascending" ? 1 : -1;
          }
          return 0;
        }

        // For non-string values (numbers, etc.)
        if (aValue < bValue) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableData;
  }, [filteredAndSortedValidators, sortConfig]);

  const [page, setPage] = useState(1);
  const pageSize = 12;
  const [transactions, setTransactions] = useState([]);

  useEffect(() => {
    const newTransactions = sortedValidators?.slice(0, page * pageSize);
    if (JSON.stringify(transactions) !== JSON.stringify(newTransactions)) {
      setTransactions(newTransactions);
    }
  }, [sortedValidators, transactions, page, pageSize]);

  useEffect(() => {
    if (page > 1) {
      fetchTransactions();
    }
  }, [page]);

  const fetchTransactions = () => {
    const newTransactions = sortedValidators?.slice(
      (page - 1) * pageSize,
      page * pageSize
    );

    if (page * pageSize > sortedValidators?.length) {
      setTransactions((prev) => [...prev, ...newTransactions]);
      setHasMore(false);
    } else {
      setTransactions((prev) => [...prev, ...newTransactions]);
    }
  };

  const handleScroll = () => {
    const div = tableDivRef.current;

    if (div) {
      if (div.scrollHeight - 300 < div.scrollTop + div.clientHeight) {
        setIsLoadingMore(true);
      }
    }
  };

  function debounce(func, delay) {
    let timeoutId;
    return function (...args) {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        func(...args);
      }, delay);
    };
  }

  useEffect(() => {
    if (view === "list" && window.location.pathname === "/all-validator") {
      const div = tableDivRef.current;
      if (div) {
        const debouncedScroll = debounce(handleScroll, 200);
        div.addEventListener("scroll", debouncedScroll);
      }

      return () => {
        if (div) {
          div.removeEventListener("scroll", debounce(handleScroll, 200));
        }
      };
    }
  }, []);

  useEffect(() => {
    if (
      isLoadingMore === true &&
      transactions?.length !== filteredAndSortedValidators?.length
    ) {
      setTimeout(() => {
        setPage((prevPage) => prevPage + 1);
        setIsLoadingMore(false);
      }, 1000);
    }
  }, [isLoadingMore]);

  const currentValidators = transactions;

  return (
    <>
      <div
        ref={tableDivRef}
        className="relative max-h-[798px] overflow-auto rounded-3xl border-[1.5px] border-primaryWhite/[0.03] bg-[#121212]"
      >
        <table className="w-full text-left">
          <thead className="border-b border-primaryWhite/[0.04] bg-black/[0.31]">
            <tr>
              <th
                scope="col"
                className="px-9 py-9"
                onClick={() => handleSort("name")}
              >
                <div className="flex items-center pl-3 gap-2 text-sm font-normal leading-4 text-white/[0.44] cursor-pointer">
                  Name
                  <span className="w-3 h-3 *:w-full *:h-full">
                    {validatorListIcons?.arrow}
                  </span>
                </div>
              </th>
              <th scope="col" className="px-9 py-9">
                <div className="flex items-center justify-center gap-2 text-sm font-normal leading-4 text-white/[0.44]">
                  Status
                </div>
              </th>
              <th
                scope="col"
                className="px-9 py-9"
                onClick={() => handleSort("commission")}
              >
                <div className="flex items-center justify-center gap-2 text-sm font-normal leading-4 text-white/[0.44] cursor-pointer">
                  Commission
                  <span className="w-3 h-3 *:w-full *:h-full">
                    {validatorListIcons?.arrow}
                  </span>
                </div>
              </th>
              {/* <th
                scope="col"
                className="px-9 py-9"
                onClick={() => handleSort("checkpoint_signed")}
              >
                <div className="flex items-center justify-center gap-2 text-sm font-normal leading-4 text-white/[0.44] cursor-pointer">
                  Checkpoints signed
                  <span className="w-3 h-3 *:w-full *:h-full">
                    {validatorListIcons?.arrow}
                  </span>
                </div>
              </th> */}
              <th
                scope="col"
                className="px-9 py-9"
                onClick={() => handleSort("apy")}
              >
                <div className="flex items-center justify-center gap-2 text-sm font-normal leading-4 text-white/[0.44] cursor-pointer">
                  APY
                  <span className="w-3 h-3 *:w-full *:h-full">
                    {validatorListIcons?.arrow}
                  </span>
                </div>
              </th>
              <th
                scope="col"
                className="px-9 py-9"
                onClick={() => handleSort("total_staked")}
              >
                <div className="flex items-center justify-center gap-2 text-sm font-normal leading-4 text-white/[0.44] cursor-pointer">
                  Stake
                  <span className="w-3 h-3 *:w-full *:h-full">
                    {validatorListIcons?.arrow}
                  </span>
                </div>
              </th>
              <th scope="col" className="px-9 py-3">
                <span className="sr-only"></span>
              </th>
            </tr>
          </thead>
          <tbody>
            {isUndefined && (
              <tr className="border-b border-white/[0.03]">
                <td className="px-9 pt-6 pb-8" colSpan="7">
                  <ClipLoaderComponent size={55} customClass="justify-center" />
                </td>
              </tr>
            )}
            {!isUndefined && currentValidators?.length === 0 && (
              <tr className="border-b border-white/[0.03]">
                <td className="px-9 py-12" colSpan="7">
                  <div className="text-sm leading-4 font-normal whitespace-nowrap flex justify-center items-center gap-3">
                    No Validators Found
                  </div>
                </td>
              </tr>
            )}
            {!isUndefined &&
              currentValidators?.length > 0 &&
              currentValidators.map((validator, index) => (
                <tr
                  key={validator?.id}
                  className={`border-b border-white/[0.03] hover:bg-[#1A1A1A80] ${validator?.status!=="inactive" ? "hover:cursor-pointer" : "hover:cursor-not-allowed"}`}
                  onClick={() => handleValidator(validator)}
                >
                  <th
                    scope="row"
                    className="px-9 pt-6 pb-8 text-base leading-5 font-medium whitespace-nowrap flex items-center gap-3"
                  >
                    <div className="flex items-center justify-center w-[54px] h-[54px] mr-1">
                      <img
                        src={getValidatorImg(validator?.name)}
                        alt=""
                        className="object-cover"
                      />
                    </div>
                    {validator?.name}
                    <span className="w-4 h-4">
                      {validatorListIcons.verificationTick}
                    </span>
                  </th>
                  <td className="px-9 pt-6 pb-8">
                    <div className="flex items-center justify-center">
                      <div
                        className={`flex items-center justify-center gap-[10px] text-sm leading-none font-normal -tracking-04 px-[14px] py-3 text-green capitalize bg-green/[0.08] rounded-xl h-9 max-w-fit ${validator?.status === "inactive" ? "opacity-50" : ""
                          }`}
                      >
                        {validator?.status !== "inactive" && (
                          <span className="w-[6px] h-[6px] rounded-full bg-green" />
                        )}
                        {validator?.status}
                      </div>
                    </div>
                  </td>
                  <td className="px-9 pt-6 pb-8">
                    <div className="flex items-center justify-center gap-2 text-base font-medium leading-5 text-primaryWhite opacity-75">
                      {validator?.commission === 0 ? "TBD" : `${formatNumber((validator?.commission * 100).toString())}%`}
                    </div>
                  </td>
                  {/* <td className="px-9 pt-6 pb-8">
                    <div className="flex items-center justify-center gap-2 text-base font-medium leading-5 text-primaryWhite opacity-75">
                      {validator?.checkpoint_signed * 100}%
                    </div>
                  </td> */}
                  <td className="px-9 pt-6 pb-8">
                    <div className="flex items-center justify-center gap-2 text-base font-medium leading-5 text-primaryWhite opacity-75">
                      {validator?.apy === 0 ? "TBD" : `${validator?.apy * 100}%`}
                    </div>
                  </td>
                  <td className="px-9 pt-6 pb-8">
                    <div className="flex items-center justify-center gap-1 text-base font-medium leading-5 text-tagcolor">
                      <span>
                        {validator?.status === "inactive" ? 0 :formatNumber(validator?.total_staked.toString())}
                      </span>
                      $VANRY
                    </div>
                  </td>
                  <td className="px-9 pt-6 pb-8 text-right">
                    <Button
                      id="delegate"
                      className={`bg-green !text-sm text-[#06080C] py-[19px] md:py-3 px-0 leading-none w-full ${validator?.status === "inactive" || validator?.commission === 1
                        ? "bg-green/[0.10] text-green/[0.22] !cursor-not-allowed"
                        : ""
                        }`}
                      disabled={validator?.status === "inactive" || validator?.commission === 1}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelegate(
                          validator?.name,
                          validator?.performance,
                          validator?.commission,
                          validator?.contract_address
                        );
                      }}
                    >
                      {validator?.commission === 0 && validator?.status === "inactive" ? "Coming Soon" :"Delegate"}
                    </Button>
                  </td>
                </tr>
              ))}
            {isLoadingMore && hasMore && currentValidators?.length > 10 && (
              <tr className="border-b border-white/[0.03]">
                <td className="px-9 py-9" colSpan="8">
                  <ClipLoaderComponent
                    size={40}
                    customClass={"justify-center"}
                  />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {/* <div className="flex item-center justify-between *:h-10 mt-[30px]">
                <div className="flex items-center gap-3 px-[10px] py-[6px] rounded-[10px] bg-[#1A1A1A] text-sm leading-none font-normal text-white">
                    <span className="text-white/50">Row Per Page:</span>{" "}
                    {rowsPerPage}
                </div>
            </div> */}
    </>
  );
}
